import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

/**
 * Top 10 Holdings Bar Chart Component
 * Displays largest holdings by value
 */
function Top10HoldingsChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="chart-empty-state">
        <p>No holdings data available</p>
      </div>
    );
  }

  // Color gradient for bars (darker for larger holdings)
  const colors = [
    '#4F46E5', '#6366F1', '#818CF8', '#A5B4FC', '#C7D2FE',
    '#E0E7FF', '#EEF2FF', '#F5F7FF', '#FAFAFF', '#FCFCFF'
  ];

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{data.name}</p>
          <p className="tooltip-value">
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'EUR'
            }).format(data.value)}
          </p>
          <p className="tooltip-percent">{data.percentage.toFixed(2)}% of portfolio</p>
        </div>
      );
    }
    return null;
  };

  // Format Y-axis values to currency
  const formatYAxis = (value) => {
    if (value >= 1000) {
      return `€${(value / 1000).toFixed(0)}k`;
    }
    return `€${value}`;
  };

  // Custom label for bars showing percentage
  const CustomLabel = ({ x, y, width, value, index }) => {
    const percentage = data[index]?.percentage || 0;
    if (percentage < 1) return null; // Don't show label if < 1%

    return (
      <text
        x={x + width + 5}
        y={y + 10}
        fill="#666"
        textAnchor="start"
        className="bar-label"
      >
        {percentage.toFixed(1)}%
      </text>
    );
  };

  return (
    <div className="chart-container">
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 5, right: 60, left: 80, bottom: 5 }}
          aria-label="Top 10 holdings bar chart"
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
          <XAxis type="number" tickFormatter={formatYAxis} />
          <YAxis
            type="category"
            dataKey="name"
            width={75}
            tick={{ fontSize: 12 }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="value" label={<CustomLabel />}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default Top10HoldingsChart;
